import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import logo from "../assets/AxisMedia.png";

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const location = useLocation();

  /* ================= NAVBAR HIDE + LOGO SCROLL ================= */
  useEffect(() => {
    const handleScroll = () => {
      const noHidePages = [
        "/login",
        "/signup",
        "/forgot-password",
        "/reset-password",
      ];

      const current = window.scrollY;

      // logo shrink
      setIsScrolled(current > 80);

      if (noHidePages.includes(location.pathname)) {
        setHidden(false);
        return;
      }

      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          setHidden(current > lastScrollY.current && current > 120);
          lastScrollY.current = current;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  /* ================= AUTO CLOSE MOBILE MENU ON PAGE CHANGE ================= */
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);



  return (
    <>
      {/* ================= HEADER ================= */}
      <header
        className={`fixed top-0 left-0 w-full z-[100]
        shadow-lg transition-all duration-300
        ${hidden ? "-translate-y-full" : "translate-y-0"}
        ${isScrolled ? "h-[72px]" : "h-[96px]"}`}
        style={{ backgroundColor: "#0ea5e9" }}
      >
        <nav className="w-full h-full">
          <div className="max-w-7xl mx-auto px-6 h-full flex items-center">

            {/* ================= LOGO ================= */}
            <NavLink to="/" className="flex items-center h-full shrink-0 mr-10">
              <img
                src={logo}
                alt="Axis Media Digital Marketing Agency"
                className={`object-contain transition-all duration-300 ${isScrolled ? "h-[100px]" : "h-[120px]"
                  }`}
              />
            </NavLink>

            {/* ================= DESKTOP MENU ================= */}
            <ul className="hidden md:flex items-center gap-8 font-semibold text-white text-[15px]">

              <li><NavLink to="/">Home</NavLink></li>

              {/* SERVICES */}
              <li
                className="relative"
                onMouseEnter={() => setOpenMenu("services")}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <NavLink to="/services-grid" className="cursor-pointer">
                  Services ▾
                </NavLink>

                <div
                  className={`absolute top-full left-0 bg-white text-gray-800 p-5 rounded-xl
                      w-[380px] shadow-xl max-h-[70vh] overflow-y-auto ${openMenu === "services" ? "block" : "hidden"}`}
                >
                  <h3 className="font-bold text-sky-600 mb-3">Our Services</h3>
                  <ul className="space-y-2 text-sm">
                    <li><NavLink to="/services/website-development">Website Development</NavLink></li>
                    <li>
                      <NavLink
                        to="/digital-marketing-company-in-delhi"
                        className="font-semibold text-sky-700"
                      >
                        Digital Marketing Company in Delhi
                      </NavLink>
                    </li>
                    <li><NavLink to="/services-grid">All Services</NavLink></li>
                    <li><NavLink to="/digital-marketing">Digital Marketing</NavLink></li>
                    <li><NavLink to="/google-ads">Google Ads</NavLink></li>
                    <li><NavLink to="/seo-services">SEO Services</NavLink></li>
                    <li><NavLink to="/branding">Branding & Graphics</NavLink></li>

                    <li><NavLink to="/seo-services-delhi">SEO Services in Delhi</NavLink></li>
                    <li><NavLink to="/google-ads-agency-delhi">Google Ads Agency in Delhi</NavLink></li>
                    <li><NavLink to="/website-development-company-delhi">Website Development Company in Delhi</NavLink></li>
                    <li><NavLink to="/social-media-marketing-delhi">Social Media Marketing in Delhi</NavLink></li>
                    <li><NavLink to="/digital-marketing-agency-janakpuri">Digital Marketing Agency in Janakpuri</NavLink></li>
                  </ul>
                </div>
              </li>

              {/* BUSINESS */}
              <li
                className="relative"
                onMouseEnter={() => setOpenMenu("business")}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <NavLink to="/industries" className="cursor-pointer">
                  Business Solutions ▾
                </NavLink>

                <div
                  className={`absolute top-full left-0 bg-white text-gray-800 p-5 rounded-xl
                  w-64 shadow-xl ${openMenu === "business" ? "block" : "hidden"}`}
                >
                  <ul className="space-y-2 text-sm">
                    <li><NavLink to="/grow-business">Grow Business</NavLink></li>
                    <li><NavLink to="/industries">Industries</NavLink></li>
                  </ul>
                </div>
              </li>

              {/* WEB DEV */}
              <li
                className="relative"
                onMouseEnter={() => setOpenMenu("web")}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <span className="cursor-pointer">Web Development ▾</span>
                <div
                  className={`absolute top-full left-0 bg-white text-gray-800 p-5 rounded-xl
                  w-64 shadow-xl ${openMenu === "web" ? "block" : "hidden"}`}
                >
                  <ul className="space-y-2 text-sm">
                    <li><NavLink to="/portfolio">Portfolio</NavLink></li>
                    <li><NavLink to="/packages">Packages</NavLink></li>
                  </ul>
                </div>
              </li>

              <li><NavLink to="/about">About Us</NavLink></li>
              <li><NavLink to="/blog">Blog</NavLink></li>
              <li><NavLink to="/contact">Contact</NavLink></li>

              <NavLink to="/contact" className="bg-blue-700 px-4 py-2 rounded-xl whitespace-nowrap">
                Get a Quote
              </NavLink>


            </ul>

            {/* ================= MOBILE ICON ================= */}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-white text-3xl"
            >
              ☰
            </button>
          </div>
        </nav>
      </header>

      {/* ================= MOBILE MENU ================= */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50">
          <div className="absolute right-0 top-0 w-72 h-full bg-white shadow-xl p-6 overflow-y-auto">

            <button
              onClick={() => setMobileOpen(false)}
              className="text-2xl mb-6"
            >
              ✕
            </button>

            <nav className="flex flex-col gap-4 font-semibold text-gray-800">

              <NavLink to="/">Home</NavLink>
              <NavLink to="/services-grid">Services</NavLink>
              <NavLink
                to="/digital-marketing-company-in-delhi"
                className="text-sky-700 font-bold"
              >
                Digital Marketing Company in Delhi
              </NavLink>
              <NavLink to="/industries">Business Solutions</NavLink>
              <NavLink to="/digital-marketing">Digital Marketing</NavLink>
              <NavLink to="/about">About Us</NavLink>
              <NavLink to="/blog">Blog</NavLink>
              <NavLink to="/contact">Contact</NavLink>


              <hr />

              <p className="font-bold text-sky-600">Services</p>
              <NavLink to="/services/website-development">Website Development</NavLink>
              <NavLink to="/google-ads">Google Ads</NavLink>
              <NavLink to="/seo-services">SEO Services</NavLink>
              <NavLink to="/branding">Branding & Graphics</NavLink>

              <NavLink to="/seo-services-delhi">SEO Services in Delhi</NavLink>
              <NavLink to="/google-ads-agency-delhi">Google Ads Agency in Delhi</NavLink>
              <NavLink to="/website-development-company-delhi">Website Development Company in Delhi</NavLink>
              <NavLink to="/social-media-marketing-delhi">Social Media Marketing in Delhi</NavLink>
              <NavLink to="/digital-marketing-agency-janakpuri">Digital Marketing Agency in Janakpuri</NavLink>

              <p className="font-bold text-sky-600 mt-3">Business Solutions</p>
              <NavLink to="/grow-business">Grow Business</NavLink>
              <NavLink to="/industries">Industries</NavLink>

              <p className="font-bold text-sky-600 mt-3">Web Development</p>
              <NavLink to="/portfolio">Portfolio</NavLink>
              <NavLink to="/packages">Packages</NavLink>

            </nav>
          </div>
        </div>
      )}
    </>
  );
}
